import { FieldTypes } from '../../constants/FieldTypes'
import { A4_DIMS_PDF_UNITS } from '../../editor/constants'
import { PatientCoverageId, PopulatedAssessment, PopulatedUser, ShortcutArgs } from '../../types'
import {
  ExternalPdfFieldType,
  InlineAttachmentPlacement,
  TemplateAttachment,
  TemplateExternalPdf,
} from '../../types/templates'
import { getInsurancePlan } from '../insurance'
import { getShortcutArgs } from './shortcutArgs'
import { getShortcutValue } from './shortcuts'

const defaultFieldSize: Record<ExternalPdfFieldType, [number, number]> = {
  [FieldTypes.TEXT]: [100, 26],
  [FieldTypes.TEXTAREA]: [190, 40],
  [FieldTypes.DATE]: [87, 26],
  [FieldTypes.SIGNATURE]: [270, 140],
  [FieldTypes.INITIALS]: [60, 26],
  [FieldTypes.FILE]: [100, 100],
  [FieldTypes.DROPDOWN]: [200, 40],
}

const defaultValueSize: Record<ExternalPdfFieldType, [number, number]> = {
  [FieldTypes.TEXT]: [100, 26],
  [FieldTypes.TEXTAREA]: [120, 64],
  [FieldTypes.DATE]: [87, 26],
  [FieldTypes.SIGNATURE]: [160, 40],
  [FieldTypes.INITIALS]: [44, 26],
  [FieldTypes.FILE]: [100, 100],
  [FieldTypes.DROPDOWN]: [200, 40],
}

export const defaultDataSize: Record<FieldTypes, [number, number]> = {
  [FieldTypes.TEXT]: [150, 18],
  [FieldTypes.HEIGHT]: [100, 18],
  [FieldTypes.TEXTAREA]: [150, 64],
  // wide enough for the long date format ("September 15, 2026") at the default 12pt font
  [FieldTypes.DATE]: [110, 18],
  [FieldTypes.SIGNATURE]: [160, 40],
  [FieldTypes.INITIALS]: [44, 18],
  [FieldTypes.ALTERNATE]: [150, 18],
  [FieldTypes.BOOLEAN]: [100, 18],
  [FieldTypes.CHECKBOX]: [100, 18],
  [FieldTypes.DATETIME]: [100, 18],
  [FieldTypes.EMAIL]: [100, 18],
  [FieldTypes.NUMBER]: [100, 18],
  [FieldTypes.PHONE]: [100, 18],
  [FieldTypes.TIME]: [100, 18],
  [FieldTypes.DROPDOWN]: [150, 18],
  [FieldTypes.FILE]: [100, 100],
  [FieldTypes.MULTIPLE_SELECT]: [100, 18],
  [FieldTypes.DYNAMIC_DROPDOWN]: [100, 18],
  [FieldTypes.DOLLAR_AMOUNT]: [100, 18],
  [FieldTypes.PERCENTAGE]: [50, 18],
  [FieldTypes.ID]: [120, 18],
  [FieldTypes.EXAM_SECTION]: [150, 36],
  [FieldTypes.BLOOD_PRESSURE]: [120, 18],
}

export const getInlineFieldSize = (attachment: TemplateAttachment<InlineAttachmentPlacement>) => {
  switch (attachment.type) {
    case 'input':
      // Radio inputs carry an explicit resize box; other input types fall back to their defaults.
      if (attachment.width != null && attachment.height != null) {
        return [attachment.width, attachment.height]
      }
      return defaultFieldSize[attachment.field.type as ExternalPdfFieldType]
    case 'data':
    case 'shape':
      return [attachment.width, attachment.height]
    case 'file':
      return A4_DIMS_PDF_UNITS
  }
}

export const getInlineValueSize = (attachment: TemplateAttachment<InlineAttachmentPlacement>) => {
  switch (attachment.type) {
    case 'input':
      if (attachment.width != null && attachment.height != null) {
        return [attachment.width, attachment.height]
      }
      return defaultValueSize[attachment.field.type as ExternalPdfFieldType]
    case 'data':
    case 'shape':
      return [attachment.width, attachment.height]
    case 'file':
      return A4_DIMS_PDF_UNITS
  }
}

// Bakes shortcut values into data attachments using the given shortcut args. Throws when a
// value cannot be resolved so the send fails loudly rather than delivering a blank document.
export const populateDataAttachmentsWithArgs = (
  shortcutArgs: ShortcutArgs,
  attachments: TemplateExternalPdf['attachments'],
) =>
  Object.entries(attachments).reduce<TemplateExternalPdf['attachments']>(
    (acc, [id, attachment]) => {
      if (attachment.type === 'data') {
        const value = getShortcutValue(
          attachment.shortcut.id,
          attachment.shortcut.path,
          shortcutArgs,
        )
        if (!value) {
          throw new Error(`Shortcut value for ${attachment.shortcut.name} not found`)
        }
        return { ...acc, [id]: { ...attachment, value } }
      }
      return { ...acc, [id]: attachment }
    },
    {},
  )

export const populateDataAttachments = (
  user: PopulatedUser,
  assessmentId: string | null,
  assessment: PopulatedAssessment | null,
  coverageId: PatientCoverageId | null,
  attachments: TemplateExternalPdf['attachments'],
) => {
  const coverage = coverageId ? getInsurancePlan(user.insurancePlans, coverageId) : null
  const shortcutArgs = getShortcutArgs(user, assessmentId, assessment, coverage)
  return populateDataAttachmentsWithArgs(shortcutArgs, attachments)
}
